import { type SchemaNames, SnapshotStore } from '@contember/bindx'
import { ContemberBindxProvider } from '@contember/bindx-react'
import { useCurrentContentGraphQlClient } from '@contember/react-client'
import { type ComponentProps, type ReactNode, useMemo } from 'react'
import { adminSchema } from './admin-schema'

// Bindx data layer fed by the authenticated content client from the
// surrounding <ContemberClient> (NPI pattern). The session token sent by the
// SDK is a placeholder the worker proxy swaps for the real token from the
// CONTEMBER_TOKEN cookie.
export function ContemberProvider({ children }: { children: ReactNode }) {
	const client = useCurrentContentGraphQlClient()
	const store = useMemo(() => new SnapshotStore(), [])
	const schema = useMemo(() => toContemberSchemaNames(adminSchema), [])
	const bindxClient = client as unknown as ComponentProps<typeof ContemberBindxProvider>['client']

	return (
		<ContemberBindxProvider client={bindxClient} schema={schema} store={store}>
			{children}
		</ContemberBindxProvider>
	)
}

function toContemberSchemaNames(schema: typeof adminSchema): SchemaNames {
	type ContemberField =
		| { type: 'column'; enumName?: string; columnType?: string }
		| { type: 'one'; entity: string; nullable?: boolean }
		| { type: 'many'; entity: string; relationKind?: 'oneHasMany' | 'manyHasMany'; nullable?: boolean }

	const entities: Record<string, { name: string; scalars: string[]; fields: Record<string, ContemberField> }> = {}
	const enums: Record<string, readonly string[]> = {}

	for (const [entityName, entityDef] of Object.entries(schema.entities)) {
		const fields: Record<string, ContemberField> = {}
		const scalars: string[] = []

		for (const [fieldName, fieldDef] of Object.entries(entityDef.fields)) {
			if (fieldDef.type === 'scalar') {
				scalars.push(fieldName)
				fields[fieldName] = { type: 'column', columnType: fieldDef.columnType }
				continue
			}

			if (fieldDef.type === 'enum') {
				scalars.push(fieldName)
				enums[fieldDef.enumName] = fieldDef.values
				fields[fieldName] = { type: 'column', enumName: fieldDef.enumName }
				continue
			}

			if (fieldDef.type === 'hasOne') {
				fields[fieldName] = {
					type: 'one',
					entity: fieldDef.target,
					nullable: fieldDef.nullable,
				}
				continue
			}

			fields[fieldName] = {
				type: 'many',
				entity: fieldDef.target,
				relationKind: fieldDef.relationKind,
				nullable: fieldDef.nullable,
			}
		}

		entities[entityName] = {
			name: entityName,
			scalars,
			fields,
		}
	}

	return { entities, enums }
}
